import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import { ICurrentUser } from '../../entities/Login/CurrentUser.ts';
import { ISignInChallenge } from '../../entities/Login/SignInChallenge.ts';

export type LoginPostModel = {
    username: string;
    password: string;
};

export type SignInTokenPostModel = {
    signInToken: string;
};

export type VerifySignInCodePostModel = {
    signInToken: string;
    code: string;
    trustBrowser: boolean;
};

export type ChangePasswordPostModel = {
    oldPassword: string;
    newPassword: string;
};

export type SetLanguagePostModel = {
    language: string;
};

export class LoginApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}`;

    public static apiUrls: {
        logout: string;
        login: string;
        changePassword: string;
        resetPassword: string;
        forgotPassword: string;
        setLanguage: string;
        verifySignInCode: string;
        sendSignInCode: string;
    } = {
        login: `${LoginApi.baseApiUrl}/Login`,
        logout: `${LoginApi.baseApiUrl}/Logout`,
        changePassword: `${LoginApi.baseApiUrl}/ChangePassword`,
        resetPassword: `${LoginApi.baseApiUrl}/ResetPassword`,
        forgotPassword: `${LoginApi.baseApiUrl}/ForgotPassword`,
        setLanguage: `${LoginApi.baseApiUrl}/SetLanguage`,
        verifySignInCode: `${LoginApi.baseApiUrl}/Login/VerifySignInCode`,
        sendSignInCode: `${LoginApi.baseApiUrl}/Login/SendSignInCode`,
    };

    public static async login(data: LoginPostModel): Promise<ICurrentUser | ISignInChallenge> {
        return this.sendApiRequest<ICurrentUser | ISignInChallenge>({ 
            method: HttpMethodEnum.POST, 
            path: this.apiUrls.login, 
            data: data,
            suppressGlobalErrorHandling: true
        });
    }

    public static async verifySignInCode(data: VerifySignInCodePostModel): Promise<ICurrentUser> {
        return this.sendApiRequest<ICurrentUser>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.verifySignInCode,
            data: data,
            suppressGlobalErrorHandling: true,
        });
    }

    public static async sendSignInCode(data: SignInTokenPostModel): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.sendSignInCode,
            data: data,
            suppressGlobalErrorHandling: true,
        });
    }

    public static async logout(): Promise<number> {
        return this.sendApiRequest({ method: HttpMethodEnum.POST, path: this.apiUrls.logout });
    }

    public static async changePassword(data: ChangePasswordPostModel): Promise<string> {
        return this.sendApiRequest<string>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.changePassword,
            data: data,
            suppressGlobalErrorHandling: true
        });
    }

    public static async resetPassword(data: { userID: number; token: string; newPassword: string }): Promise<string> {
        return this.sendApiRequest<string>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.resetPassword,
            data: data,
            suppressGlobalErrorHandling: true
        });
    }

    public static async forgotPassword(data: string): Promise<string> {
        return this.sendApiRequest<string>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.forgotPassword,
            data: data,
            suppressGlobalErrorHandling: true
        });
    }

    public static async setLanguage(data: SetLanguagePostModel): Promise<string | undefined> {
        return this.sendApiRequest<string>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.setLanguage,
            data: data,
        });
    }
}
